import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { applyThemeCssVariables } from './css-variables';
import { themeModes, lightThemeTokens, darkThemeTokens, } from './design-tokens';
const ThemeContext = createContext(undefined);
const getSystemPreferredMode = () => {
    if (typeof window === 'undefined')
        return 'light';
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
};
const resolveInitialMode = (explicitMode, storageKey) => {
    if (explicitMode)
        return explicitMode;
    if (typeof window === 'undefined')
        return 'light';
    if (storageKey) {
        try {
            const stored = window.localStorage.getItem(storageKey);
            if (stored === 'light' || stored === 'dark')
                return stored;
        }
        catch {
            // localStorage unavailable (private browsing, iframe restrictions)
        }
    }
    return getSystemPreferredMode();
};
export const ThemeProvider = ({ children, mode, storageKey = 'loci-theme', followSystem, applyCssVariables = true, }) => {
    const [currentMode, setCurrentMode] = useState(mode ?? 'light');
    const [isHydrated, setIsHydrated] = useState(false);
    useEffect(() => {
        if (!isHydrated) {
            const resolved = resolveInitialMode(mode, storageKey);
            setCurrentMode(resolved);
            setIsHydrated(true);
        }
    }, [mode, storageKey, isHydrated]);
    // Controlled use case: keep currentMode in sync when the mode prop changes after hydration
    // (e.g. Storybook theme toolbar driving ThemeProvider externally).
    useEffect(() => {
        if (mode !== undefined && isHydrated) {
            setCurrentMode(mode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);
    useEffect(() => {
        if (storageKey) {
            try {
                window.localStorage.setItem(storageKey, currentMode);
            }
            catch {
                // localStorage unavailable (private browsing, quota exceeded)
            }
        }
    }, [currentMode, storageKey]);
    useEffect(() => {
        if (!applyCssVariables)
            return;
        applyThemeCssVariables(currentMode, { setDataAttribute: true });
    }, [applyCssVariables, currentMode]);
    useEffect(() => {
        // Controlled `mode` always wins. Otherwise follow the OS only when the
        // consumer opted in — defaulting to the historical "no persistence" case
        // (`storageKey={null}`) so existing callers keep their behavior.
        const shouldFollowSystem = followSystem ?? (!mode && !storageKey);
        if (mode || !shouldFollowSystem)
            return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            setCurrentMode(e.matches ? 'dark' : 'light');
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, [followSystem, mode, storageKey]);
    const theme = useMemo(() => themeModes[currentMode], [currentMode]);
    const value = useMemo(() => ({
        ...theme,
        mode: currentMode,
        setMode: setCurrentMode,
        toggleMode: () => setCurrentMode((prev) => (prev === 'light' ? 'dark' : 'light')),
    }), [theme, currentMode]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
};
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
export const availableThemes = {
    light: lightThemeTokens,
    dark: darkThemeTokens,
};
